class ProductZoom extends HTMLElement {
  #dialog = null;
  #closeButton = null;
  #slider = null;
  #thumbnailSlider = null;
  #gallery = null;
  #onTriggerClickBound = null;
  #onCloseClickBound = null;
  #onDialogClickBound = null;
  #onDialogCloseBound = null;
  #onKeydownBound = null;
  #animationDuration = 0.3;
  #defaultDrag = true;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-ref="zoom-dialog"]');
    if (!this.#dialog) return;

    this.#closeButton = this.querySelector('[data-ref="zoom-close"]');
    this.#gallery = this.closest('product-media-gallery');

    const speed = getComputedStyle(this).getPropertyValue('--animation-speed').trim();
    if (speed) this.#animationDuration = parseFloat(speed.replace('s', '')) || this.#animationDuration;

    this.#initSliders();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#unbindEvents();

    if (this.#slider) {
      this.#slider.destroy(true);
      this.#slider = null;
    }

    if (this.#thumbnailSlider) {
      this.#thumbnailSlider.destroy(true);
      this.#thumbnailSlider = null;
    }
  }

  #initSliders() {
    const sliderEl = this.querySelector('[data-ref="zoom-slider"]');
    if (!sliderEl) return;

    const slidesCount = sliderEl.querySelectorAll('.product-zoom__slide').length;
    const hasMultiple = slidesCount > 1;

    this.#defaultDrag = hasMultiple;

    this.#slider = new Splide(sliderEl, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      rewind: hasMultiple,
      gap: 0,
      pagination: false,
      arrows: hasMultiple,
      drag: this.#defaultDrag,
      keyboard: false,
      lazyLoad: false,
      snap: true,
      speed: 400,
      flickMaxPages: 1,
      flickPower: 400,
      mediaQuery: 'min',
    });

    const thumbsEl = this.querySelector('[data-ref="zoom-thumbnails"]');

    if (thumbsEl && hasMultiple) {
      this.#thumbnailSlider = new Splide(thumbsEl, {
        fixedWidth: 70,
        fixedHeight: 70,
        gap: 8,
        rewind: false,
        pagination: false,
        isNavigation: true,
        arrows: false,
        drag: 'free',
        breakpoints: {
          750: {
            fixedWidth: 50,
            fixedHeight: 50,
          },
        },
      });

      this.#slider.sync(this.#thumbnailSlider);
    }

    this.#slider.mount();

    if (this.#thumbnailSlider) {
      this.#thumbnailSlider.mount();
    }
  }

  #bindEvents() {
    this.#onTriggerClickBound = this.#onTriggerClick.bind(this);
    this.#onCloseClickBound = this.#close.bind(this);
    this.#onDialogClickBound = this.#onDialogClick.bind(this);
    this.#onDialogCloseBound = this.#onDialogClose.bind(this);
    this.#onKeydownBound = this.#onKeydown.bind(this);

    if (this.#gallery) {
      this.#gallery.addEventListener('click', this.#onTriggerClickBound);
    }

    if (this.#closeButton) {
      this.#closeButton.addEventListener('click', this.#onCloseClickBound);
    }

    this.#dialog.addEventListener('click', this.#onDialogClickBound);
    this.#dialog.addEventListener('close', this.#onDialogCloseBound);
  }

  #unbindEvents() {
    if (this.#gallery && this.#onTriggerClickBound) {
      this.#gallery.removeEventListener('click', this.#onTriggerClickBound);
    }

    if (this.#closeButton && this.#onCloseClickBound) {
      this.#closeButton.removeEventListener('click', this.#onCloseClickBound);
    }

    if (this.#dialog) {
      if (this.#onDialogClickBound) this.#dialog.removeEventListener('click', this.#onDialogClickBound);
      if (this.#onDialogCloseBound) this.#dialog.removeEventListener('close', this.#onDialogCloseBound);
    }

    if (this.#onKeydownBound) {
      document.removeEventListener('keydown', this.#onKeydownBound);
    }
  }

  #onTriggerClick(event) {
    const trigger = event.target.closest('[data-zoom-trigger]');
    if (!trigger) return;
    if (!this.#gallery || !this.#gallery.contains(trigger)) return;

    event.preventDefault();
    const mediaId = trigger.getAttribute('data-zoom-media-id');
    this.#open(mediaId);
  }

  #open(mediaId) {
    if (!this.#dialog) return;

    if (mediaId && this.#slider) {
      const slides = Array.from(this.querySelectorAll('.product-zoom__slide')).filter((slide) => !slide.classList.contains('splide__slide--clone'));
      const targetIndex = slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
      if (targetIndex >= 0) this.#slider.go(targetIndex);
    }

    this.#dialog.showModal();
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.#onKeydownBound);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.#slider) this.#slider.refresh();
        if (this.#thumbnailSlider) this.#thumbnailSlider.refresh();
      });
    });
  }

  #close(event) {
    if (event) event.preventDefault();
    if (!this.#dialog || !this.#dialog.open) return;

    this.#dialog.classList.add('product-zoom--closing');

    const handleAnimationEnd = () => {
      this.#dialog.classList.remove('product-zoom--closing');
      this.#dialog.removeEventListener('animationend', handleAnimationEnd);
      this.#dialog.close();
    };

    this.#dialog.addEventListener('animationend', handleAnimationEnd);

    setTimeout(() => {
      if (this.#dialog.classList.contains('product-zoom--closing')) handleAnimationEnd();
    }, this.#animationDuration * 1000);
  }

  #onDialogClose() {
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.#onKeydownBound);
  }

  #onDialogClick(event) {
    if (event.target.closest('.product-zoom__slider, .product-zoom__close, .product-zoom__thumbnails')) return;
    this.#close();
  }

  #onKeydown(event) {
    if (!this.#dialog || !this.#dialog.open) return;

    if (event.key === 'ArrowLeft' && this.#slider) {
      event.preventDefault();
      this.#slider.go('<');
    } else if (event.key === 'ArrowRight' && this.#slider) {
      event.preventDefault();
      this.#slider.go('>');
    }
  }
}

if (!customElements.get('product-zoom')) {
  customElements.define('product-zoom', ProductZoom);
}

/* Visionneuse : le bouton retour du navigateur (ou le geste retour sur mobile) ferme la photo agrandie
   au lieu de quitter la fiche produit (31/10/2026). Ouvrir la visionneuse ajoute une étape à l'historique,
   sans changer l'adresse ; la refermer autrement (croix, Échap, clic à côté) retire cette étape. */
(function () {
  if (window.__zoomRetour) return;
  window.__zoomRetour = true;
  var SEL = 'dialog.pmg-lightbox, dialog.product-zoom';
  var CROIX = '.pmg-lightbox__close, [data-ref="zoom-close"]';
  var empilee = false, parRetour = false, retourEnCours = false;

  function ouverte() {
    return document.querySelector('dialog.pmg-lightbox[open], dialog.product-zoom[open]');
  }

  function empiler() {
    if (empilee || retourEnCours) return;
    history.pushState(Object.assign({}, history.state, { zoomPhoto: true }), '');
    empilee = true;
  }

  new MutationObserver(function (list) {
    list.forEach(function (m) {
      var dlg = m.target;
      if (!dlg.matches || !dlg.matches(SEL)) return;
      if (m.oldValue === null) { empiler(); return; }
      if (parRetour) { parRetour = false; return; }
      if (empilee) { empilee = false; retourEnCours = true; history.back(); }
    });
  }).observe(document.body, { subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['open'] });

  window.addEventListener('popstate', function () {
    if (retourEnCours) {
      retourEnCours = false;
      if (ouverte()) empiler();
      return;
    }
    if (!empilee) return;
    empilee = false;
    var dlg = ouverte();
    if (!dlg) return;
    parRetour = true;
    var croix = dlg.querySelector(CROIX);
    if (croix) croix.click(); else dlg.close();
  });
})();

/* Visionneuse sur téléphone et tablette : la page derrière la photo agrandie ne défile plus (31/10/2026).
   Sur iPhone, la page continue de défiler malgré overflow: hidden ; on la fige à sa position
   pendant que la photo est ouverte, puis on la remet exactement au même endroit. */
(function () {
  if (window.__zoomFige) return;
  window.__zoomFige = true;
  var SEL = 'dialog.pmg-lightbox, dialog.product-zoom';
  var y = 0, figee = false;

  function figer() {
    if (figee || window.innerWidth >= 990) return;
    figee = true;
    y = window.scrollY;
    var s = document.body.style;
    s.position = 'fixed';
    s.top = -y + 'px';
    s.left = '0';
    s.right = '0';
  }

  function liberer() {
    if (!figee) return;
    figee = false;
    var s = document.body.style;
    s.position = s.top = s.left = s.right = '';
    var html = document.documentElement, avant = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    html.style.scrollBehavior = avant;
  }

  new MutationObserver(function (list) {
    list.forEach(function (m) {
      if (!m.target.matches || !m.target.matches(SEL)) return;
      if (m.oldValue === null) figer(); else liberer();
    });
  }).observe(document.body, { subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['open'] });
})();
